import {MarkdownViewer} from '@github-ui/markdown-viewer'
import {useIgnoreKeyboardActionsWhileComposing} from '@github-ui/use-ignore-keyboard-actions-while-composing'
import {useSafeAsyncCallback} from '@github-ui/use-safe-async-callback'
import {useSyntheticChange} from '@github-ui/use-synthetic-change'
import {VisuallyHidden} from '@github-ui/visually-hidden'
import {StopIcon} from '@primer/octicons-react'
import {Box, Flash, Octicon, useResizeObserver, type SxProp} from '@primer/react'
import {useSlots} from '@primer/react/drafts'
import useIsomorphicLayoutEffect from '@primer/react/lib-esm/utils/useIsomorphicLayoutEffect'
import {
  forwardRef,
  isValidElement,
  useCallback,
  useEffect,
  useId,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react'
import {Actions} from './Actions'
import {ErrorMessage} from './ErrorMessage'
import {CoreFooter, Footer} from './Footer'
import {FormattingTools} from './FormattingTools'
import {Label} from './Label'
import {MarkdownEditorContext} from './MarkdownEditorContext'
import {MarkdownInput} from './MarkdownInput'
import {SavedRepliesContext, type SavedRepliesHandle, type SavedReply} from './SavedReplies'
import {CoreToolbar, DefaultToolbarButtons, Toolbar} from './Toolbar'
import {ViewSwitch, type MarkdownViewMode} from './ViewSwitch'
import type {SuggestionOptions} from './suggestions'
import type {Emoji} from './suggestions/use-emoji-suggestions'
import type {Mentionable} from './suggestions/use-mention-suggestions'
import type {Reference} from './suggestions/use-reference-suggestions'
import {useFileHandling, type FileType, type FileUploadResult} from './use-file-handling'
import {useIndenting} from './use-indenting'
import {useListEditing} from './use-list-editing'
import {isModifierKey} from './utils'
import type {SafeHTMLString} from '@github-ui/safe-html'

export type MarkdownEditorProps = SxProp & {
  /** Current value of the editor as a multiline markdown string. */
  value: string
  /** Called when the value changes. */
  onChange: (newMarkdown: string) => void
  /**
   * Accepts Markdown and returns rendered HTML. To prevent XSS attacks,
   * the HTML should be sanitized and/or come from a trusted source.
   */
  onRenderPreview: (markdown: string) => Promise<SafeHTMLString>
  children: React.ReactNode
  /** Disable the editor and all related buttons. Users can still switch between preview & edit modes. */
  disabled?: boolean
  /** Placeholder text to show when the editor is empty. By default, no placeholder will be shown. */
  placeholder?: string
  /** Maximum number of characters the markdown can hold (includes formatting characters like `*`). */
  maxLength?: number
  /**
   * Force the editor to take up the full height of the container and disallow resizing. Only
   * use when the container height is tall enough that the user will never want to expand the
   * input further, ie when it takes the full height of the viewport.
   */
  fullHeight?: boolean
  /** ID of the describing element. */
  'aria-describedby'?: string
  /** ID of the labelling element. */
  labelledBy?: string
  /** Optionally control the view mode. If uncontrolled, leave this `undefined`. */
  viewMode?: MarkdownViewMode
  /** If `viewMode` is controlled, this will be called on change. */
  onChangeViewMode?: (newViewMode: MarkdownViewMode) => void
  /**
   * Called when the user presses `Ctrl`/`Cmd` + `Enter`. Should almost always be wired to
   * the same event as clicking the primary `actionButton`.
   */
  onPrimaryAction?: () => void
  /**
   * Minimum number of visible lines of text in the editor.
   * @default 5
   */
  minHeightLines?: number
  /**
   * Maximum number of visible lines of text in the editor. Has no effect if `fullHeight = true`.
   * @default 35
   */
  maxHeightLines?: number
  /**
   * Array of all possible emojis to suggest. Leave `undefined` to disable emoji autocomplete.
   * For lazy-loading suggestions, an async function can be provided instead.
   */
  emojiSuggestions?: SuggestionOptions<Emoji>
  /**
   * Array of all possible mention suggestions. Leave `undefined` to disable `@`-mention autocomplete.
   * For lazy-loading suggestions, an async function can be provided instead.
   */
  mentionSuggestions?: SuggestionOptions<Mentionable>
  /**
   * Array of all possible references to suggest. Leave `undefined` to disable `#`-reference autocomplete.
   * For lazy-loading suggestions, an async function can be provided instead.
   */
  referenceSuggestions?: SuggestionOptions<Reference>
  /**
   * Uploads a file to a hosting service and returns the URL. If not provided, file uploads
   * will be disabled.
   */
  onUploadFile?: (file: File) => Promise<FileUploadResult>
  /**
   * Array of allowed file types. If `onUploadFile` is defined but this array is not, all
   * file types will be accepted. You can still reject file types by rejecting the `onUploadFile`
   * promise, but setting this array provides a better user experience by preventing the
   * upload in the first place.
   */
  acceptedFileTypes?: FileType[]
  /** Control whether the editor font is monospace. */
  monospace?: boolean
  /** Control whether the input is required. */
  required?: boolean
  /** The name that will be given to the `textarea`. */
  name?: string
  /** To enable the saved replies feature, provide an array of replies. */
  savedReplies?: SavedReply[]
  /**
   * Control whether URLs are pasted as plain text instead of as formatted links (if the
   * user has selected some text before pasting). Defaults to `false` (URLs will paste as
   * links). This should typically be controlled by user settings.
   *
   * Users can always toggle this behavior by holding `shift` when pasting.
   */
  pasteUrlsAsPlainText?: boolean
  /**
   * Optional error message related to the markdown editor content. Displayed directly
   * below editor. Useful for showing validation or other errors related directly to the
   * markdown provided by the user.
   *
   * Note: This is separate from the error message related to drag-and-drop errors.
   */
  errorMessage?: string
  /** Use hovercards for team mentions in preview mode */
  teamHovercardsEnabled?: boolean
}

const handleBrand = Symbol()

export interface MarkdownEditorHandle {
  /** Focus on the markdown textarea (has no effect in preview mode). */
  focus: (options?: FocusOptions) => void
  /** Scroll to the editor. */
  scrollIntoView: (options?: ScrollIntoViewOptions) => void
  /**
   * This 'fake' member prevents other types from being assigned to this, thus
   * disallowing broader ref types like `HTMLTextAreaElement`.
   * @private
   */
  [handleBrand]: undefined
}

const a11yOnlyStyle = {clipPath: 'Circle(0)', position: 'absolute'} as const

const CONDENSED_WIDTH_THRESHOLD = 675

/**
 * We want to switch editors from preview mode on cmd/ctrl+shift+P. But in preview mode,
 * there's no input to focus so we have to bind the event to the document. If there are
 * multiple editors, we want the most recent one to switch to preview mode to be the one
 * that we switch back to edit mode, so we maintain a LIFO stack of IDs of editors in
 * preview mode.
 */
let editorsInPreviewMode: string[] = []

/**
 * Markdown textarea with controls & keyboard shortcuts.
 */
const MarkdownEditor = forwardRef<MarkdownEditorHandle, MarkdownEditorProps>(
  (
    {
      value,
      onChange,
      disabled = false,
      placeholder,
      maxLength,
      'aria-describedby': describedBy,
      labelledBy,
      fullHeight,
      onRenderPreview,
      sx,
      onPrimaryAction,
      viewMode: controlledViewMode,
      onChangeViewMode: controlledSetViewMode,
      minHeightLines = 5,
      maxHeightLines = 35,
      emojiSuggestions,
      mentionSuggestions,
      referenceSuggestions,
      onUploadFile,
      acceptedFileTypes,
      monospace = false,
      required = false,
      name,
      children,
      savedReplies,
      pasteUrlsAsPlainText = false,
      errorMessage,
      teamHovercardsEnabled = false,
    },
    ref,
  ) => {
    const [slots, childrenWithoutSlots] = useSlots(children, {
      toolbar: Toolbar,
      actions: Actions,
      label: Label,
      footer: Footer,
    })
    const [uncontrolledViewMode, uncontrolledSetViewMode] = useState<MarkdownViewMode>('edit')
    const [view, setView] =
      controlledViewMode === undefined
        ? [uncontrolledViewMode, uncontrolledSetViewMode]
        : [controlledViewMode, controlledSetViewMode]

    const [html, setHtml] = useState<SafeHTMLString | null>(null)
    const safeSetHtml = useSafeAsyncCallback(setHtml)

    const previewStale = useRef(true)
    useEffect(() => {
      previewStale.current = true
    }, [value])
    const loadPreview = async () => {
      if (!previewStale.current) return
      previewStale.current = false // set to false before the preview is rendered to prevent multiple concurrent calls
      safeSetHtml(null)
      safeSetHtml(await onRenderPreview(value))
    }

    useEffect(() => {
      // we have to be careful here - loading preview sets state which causes a render which can cause an infinite loop,
      // however that should be prevented by previewStale.current being set immediately in loadPreview
      if (view === 'preview' && previewStale.current) loadPreview()
    })

    const inputRef = useRef<HTMLTextAreaElement>(null)
    useImperativeHandle(
      ref,
      () =>
        ({
          focus: opts => inputRef.current?.focus(opts),
          scrollIntoView: opts => containerRef.current?.scrollIntoView(opts),
        }) as MarkdownEditorHandle,
    )

    const inputHeight = useRef(0)
    if (inputRef.current && inputRef.current.offsetHeight) inputHeight.current = inputRef.current.offsetHeight

    const onInputChange = useCallback(
      (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        onChange(e.target.value)
      },
      [onChange],
    )

    const emitChange = useSyntheticChange({inputRef, fallbackEventHandler: onInputChange})

    const fileHandler = useFileHandling({
      emitChange,
      value,
      inputRef,
      disabled,
      onUploadFile,
      acceptedFileTypes,
    })

    const listEditor = useListEditing({emitChange})
    const indenter = useIndenting({emitChange})

    const formattingToolsRef = useRef<FormattingTools>(null)

    // use state instead of ref since we need to recalculate when the element mounts
    const containerRef = useRef<HTMLDivElement>(null)

    const [condensed, setCondensed] = useState(false)
    const onResize = useCallback(
      // it's fine that this isn't debounced because calling setCondensed with the current value will not trigger a render
      () => setCondensed(containerRef.current !== null && containerRef.current.clientWidth < CONDENSED_WIDTH_THRESHOLD),
      [],
    )
    useResizeObserver(onResize, containerRef)

    // workaround for Safari bug where layout is otherwise not recalculated
    useIsomorphicLayoutEffect(() => {
      const container = containerRef.current
      if (!container) return

      const parent = container.parentElement
      const nextSibling = containerRef.current.nextSibling
      parent?.removeChild(container)
      parent?.insertBefore(container, nextSibling)
    }, [condensed])

    // the ID must be unique for each instance while remaining constant across renders
    const id = useId()
    const descriptionId = `${id}-description`

    const savedRepliesRef = useRef<SavedRepliesHandle>(null)

    const savedRepliesContext = useMemo(
      () =>
        savedReplies
          ? {
              savedReplies,
              onSelect: (reply: SavedReply) => {
                // need to wait a tick to run after the selectmenu finishes closing
                requestAnimationFrame(() => emitChange(reply.content))
              },
              ref: savedRepliesRef,
            }
          : null,
      [emitChange, savedReplies],
    )

    const inputCompositionProps = useIgnoreKeyboardActionsWhileComposing(
      (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
        const format = formattingToolsRef.current
        if (disabled) return

        // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
        if (e.ctrlKey && e.key === '.') {
          // saved replies are always Control, even on Mac
          savedRepliesRef.current?.openMenu()
          e.preventDefault()
          e.stopPropagation()
        } else if (isModifierKey(e)) {
          // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
          if (e.key === 'Enter') onPrimaryAction?.()
          // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
          else if (e.key === 'b') format?.bold()
          // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
          else if (e.key === 'i') format?.italic()
          // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
          else if (e.shiftKey && e.key === '.') format?.quote()
          // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
          else if (e.key === 'e') format?.code()
          // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
          else if (e.key === 'k') format?.link()
          // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
          else if (e.key === '8') format?.unorderedList()
          // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
          else if (e.shiftKey && e.key === '7') format?.orderedList()
          // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
          else if (e.shiftKey && e.key === 'l') format?.taskList()
          // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
          else if (e.shiftKey && e.key === 'p') setView?.('preview')
          else return

          e.preventDefault()
          e.stopPropagation()
        } else {
          listEditor.onKeyDown(e)
          indenter.onKeyDown(e)
        }
      },
    )

    useEffect(() => {
      if (view === 'preview') {
        editorsInPreviewMode.push(id)

        const handler = (e: KeyboardEvent) => {
          if (
            !e.defaultPrevented &&
            editorsInPreviewMode.at(-1) === id &&
            isModifierKey(e) &&
            e.shiftKey &&
            // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
            e.key === 'p'
          ) {
            setView?.('edit')
            setTimeout(() => inputRef.current?.focus())
            e.preventDefault()
          }
        }
        document.addEventListener('keydown', handler)

        return () => {
          document.removeEventListener('keydown', handler)
          // Performing the filtering in the cleanup callback allows it to happen also when
          // the user clicks the toggle button, not just on keyboard shortcut
          editorsInPreviewMode = editorsInPreviewMode.filter(id_ => id_ !== id)
        }
      }
    }, [view, setView, id])

    // If we don't memoize the context object, every child will rerender on every render even if memoized
    const context = useMemo(
      () => ({
        disabled,
        formattingToolsRef,
        condensed,
        required,
        fileDraggedOver: fileHandler?.isDraggedOver ?? false,
        fileUploadProgress: fileHandler?.uploadProgress,
        uploadButtonProps: fileHandler?.clickTargetProps ?? null,
        errorMessage: fileHandler?.errorMessage,
        previewMode: view === 'preview',
      }),
      [
        disabled,
        condensed,
        required,
        fileHandler?.isDraggedOver,
        fileHandler?.uploadProgress,
        fileHandler?.clickTargetProps,
        fileHandler?.errorMessage,
        view,
      ],
    )

    // We are using MarkdownEditorContext instead of the built-in Slots context because Slots' context is not typesafe
    return (
      <MarkdownEditorContext.Provider value={context}>
        <fieldset
          aria-disabled={disabled /* if we set disabled={true}, we can't enable the buttons that should be enabled */}
          aria-describedby={describedBy ? `${descriptionId} ${describedBy}` : descriptionId}
          style={{appearance: 'none', border: 'none', minInlineSize: 'auto', height: fullHeight ? '100%' : undefined}}
        >
          <FormattingTools ref={formattingToolsRef} forInputId={id} />
          <div style={{display: 'none'}}>{childrenWithoutSlots}</div>

          {slots.label}

          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
            }}
            ref={containerRef}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                width: '100%',
                borderColor: 'border.default',
                borderWidth: 1,
                borderStyle: 'solid',
                borderRadius: 2,
                height: fullHeight ? '100%' : undefined,
                minInlineSize: 'auto',
                bg: 'canvas.default',
                color: disabled ? 'fg.subtle' : 'fg.default',
                '&: focus-within':
                  view === 'edit'
                    ? {
                        outline: '2px solid',
                        outlineColor: 'accent.emphasis',
                        outlineOffset: '-1px',
                      }
                    : {},
                ...sx,
              }}
            >
              <VisuallyHidden id={descriptionId} aria-live="polite">
                Markdown input:
                {view === 'preview' ? ' preview mode selected.' : ' edit mode selected.'}
              </VisuallyHidden>
              <Box
                sx={{
                  display: 'flex',
                  backgroundColor: 'canvas.subtle',
                  borderTopLeftRadius: 2,
                  borderTopRightRadius: 2,
                  justifyContent: 'space-between',
                }}
                as="header"
              >
                <Box
                  sx={{
                    ml: '-1px',
                    mt: '-1px',
                    display: 'flex',
                    alignItems: 'flex-end',
                    flexGrow: 1,
                    flexBasis: 0,
                  }}
                >
                  <ViewSwitch
                    selectedView={view}
                    onViewSelect={setView}
                    disabled={fileHandler?.uploadProgress !== undefined}
                    onLoadPreview={loadPreview}
                  />
                  <Box
                    sx={{
                      borderBottom: '1px solid',
                      borderBottomColor: 'border.subtle',
                      flexGrow: 1,
                    }}
                  />
                </Box>

                <SavedRepliesContext.Provider value={savedRepliesContext}>
                  {view === 'edit' &&
                    (slots.toolbar ?? (
                      <CoreToolbar>
                        <DefaultToolbarButtons />
                      </CoreToolbar>
                    ))}
                </SavedRepliesContext.Provider>
              </Box>
              <MarkdownInput
                value={value}
                onChange={onInputChange}
                emojiSuggestions={emojiSuggestions}
                mentionSuggestions={mentionSuggestions}
                referenceSuggestions={referenceSuggestions}
                disabled={disabled}
                placeholder={placeholder}
                labelledBy={labelledBy}
                id={id}
                maxLength={maxLength}
                ref={inputRef}
                fullHeight={fullHeight}
                isDraggedOver={fileHandler?.isDraggedOver ?? false}
                minHeightLines={minHeightLines}
                maxHeightLines={maxHeightLines}
                visible={view === 'edit'}
                monospace={monospace}
                required={required}
                name={name}
                pasteUrlsAsPlainText={pasteUrlsAsPlainText}
                {...inputCompositionProps}
                {...fileHandler?.pasteTargetProps}
                {...fileHandler?.dropTargetProps}
              />
              {view === 'edit' && fileHandler?.errorMessage && <ErrorMessage message={fileHandler.errorMessage} />}
              {view === 'preview' && (
                <Box
                  sx={{
                    p: 3,
                    overflow: 'auto',
                    height: fullHeight ? '100%' : undefined,
                    minHeight: inputHeight.current,
                    boxSizing: 'border-box',
                  }}
                  aria-live="polite"
                  tabIndex={-1}
                >
                  <h2 style={a11yOnlyStyle}>Rendered Markdown Preview</h2>
                  <MarkdownViewer
                    verifiedHTML={html || ('Nothing to preview' as SafeHTMLString)}
                    loading={html === null}
                    teamHovercardsEnabled={teamHovercardsEnabled}
                    openLinksInNewTab
                  />
                </Box>
              )}
            </Box>
            {errorMessage && (
              <Flash sx={{my: 2}} variant="danger">
                <Octicon icon={StopIcon} className="mr-2" />
                {errorMessage}
              </Flash>
            )}
            {slots.footer ?? <CoreFooter>{isValidElement(slots.actions) && slots.actions.props.children}</CoreFooter>}
          </Box>
        </fieldset>
      </MarkdownEditorContext.Provider>
    )
  },
)
MarkdownEditor.displayName = 'MarkdownEditor'

export default MarkdownEditor
